import { useEffect } from 'react';

interface SeoOptions {
  title: string;
  description?: string;
  /** Absolute canonical URL, e.g. https://agent-mesh.org/compare/zapier */
  canonical?: string;
  /** schema.org JSON-LD — one object or several, injected for this page. */
  jsonLd?: object | object[];
}

function upsertMeta(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Sets per-route SEO metadata (title, description, canonical, JSON-LD) and
 * restores the previous values on unmount. Client-side, but crawlers execute JS.
 */
export function useSeo({ title, description, canonical, jsonLd }: SeoOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${title} — AgentMesh`;

    const descEl = description
      ? (upsertMeta('meta[name="description"]', () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'description');
          return m;
        }) as HTMLMetaElement)
      : null;
    const prevDesc = descEl?.getAttribute('content') ?? null;
    if (descEl && description) descEl.setAttribute('content', description);

    const canonEl = canonical
      ? (upsertMeta('link[rel="canonical"]', () => {
          const l = document.createElement('link');
          l.setAttribute('rel', 'canonical');
          return l;
        }) as HTMLLinkElement)
      : null;
    const prevCanon = canonEl?.getAttribute('href') ?? null;
    if (canonEl && canonical) canonEl.setAttribute('href', canonical);

    const ldEls: HTMLScriptElement[] = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-seo', 'route');
        el.textContent = JSON.stringify(block);
        document.head.appendChild(el);
        ldEls.push(el);
      }
    }

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute('content', prevDesc);
      if (canonEl && prevCanon !== null) canonEl.setAttribute('href', prevCanon);
      ldEls.forEach((el) => el.remove());
    };
  }, [title, description, canonical, jsonLd]);
}
